import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaLinkedinIn,
  FaInstagram,
  FaFacebookF
} from "react-icons/fa";

import "../../assets/css/footer.css";
import bethelLogo from "../../assets/img/bethel-logo.jpg";

const companyLinks = [
  { label: "About Us", path: "/about-us" },
  { label: "Our Clients", path: "/clients" },
  { label: "Contact Us", path: "/contact-us" }
];

const capabilityLinks = [
  { label: "AI Governance & Quantum-Ready Cybersecurity", path: "/services/ai-governance-cybersecurity" },
  { label: "Cybersecurity Solutions", path: "/services/cybersecurity-solutions" },
  { label: "Change Management & IT Service Management", path: "/services/change-and-IT-management" },
  { label: "Enterprise Services", path: "/services/enterprise-services" },
  { label: "LscNexus Transportation & Mobility Solutions", path: "/services/transportation-solutions" }
];

const socialLinks = [
  {
    label: "Facebook",
    url: "https://www.facebook.com/people/Bethel-Innovations-LLC/100092667889272/",
    icon: FaFacebookF
  },
  {
    label: "LinkedIn",
    url: "https://www.linkedin.com/company/bethelinnovations",
    icon: FaLinkedinIn
  },
  {
    label: "Instagram",
    url: "https://www.instagram.com/bethel_innovations/",
    icon: FaInstagram
  }
];

function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="site-footer">
      {/* CONTACT CTA */}
      <div className="site-footer__cta">
        <div className="site-footer__container site-footer__cta-layout">
          <div className="site-footer__cta-content">
            <span className="site-footer__cta-eyebrow">
              Partner With Bethel Innovations
            </span>

            <h2>Ready to advance your technology mission?</h2>

            <p>
              Connect with our team to discuss your organization’s IT,
              cybersecurity, modernization, or program-support requirements.
            </p>
          </div>

          <Link to="/contact-us" className="site-footer__cta-button">
            Start a Conversation
            <FaArrowRight aria-hidden="true" />
          </Link>
        </div>
      </div>

      {/* PRIMARY FOOTER */}
      <div className="site-footer__main">
        <div className="site-footer__container">
          <div className="site-footer__grid">
            {/* COMPANY */}
            <div className="site-footer__company">
              <Link
                to="/"
                className="site-footer__logo-link"
                aria-label="Bethel Innovations home"
              >
                <img
                  src={bethelLogo}
                  alt="Bethel Innovations"
                  className="site-footer__logo"
                />
              </Link>

              <p className="site-footer__description">
                Delivering secure, scalable, and mission-focused technology
                solutions for government agencies, government contractors,
                nonprofits, and enterprise organizations.
              </p>

              <div
                className="site-footer__socials"
                aria-label="Bethel Innovations social media"
              >
                {socialLinks.map((social) => {
                  const Icon = social.icon;

                  return (
                    <a
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="site-footer__social-link"
                      aria-label={`Follow Bethel Innovations on ${social.label}`}
                    >
                      <Icon aria-hidden="true" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* COMPANY LINKS */}
            <nav
              className="site-footer__column"
              aria-labelledby="footer-company-heading"
            >
              <h3 id="footer-company-heading">Company</h3>

              <ul className="site-footer__links">
                {companyLinks.map((link) => (
                  <li key={link.label}>
                    <Link to={link.path}>
                      <span aria-hidden="true">›</span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* CAPABILITIES */}
            <nav
              className="site-footer__column"
              aria-labelledby="footer-capabilities-heading"
            >
              <h3 id="footer-capabilities-heading">Services</h3>

              <ul className="site-footer__links">
                {capabilityLinks.map((link) => (
                  <li key={link.label}>
                    <Link to={link.path}>
                      <span aria-hidden="true">›</span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* CONTACT */}
            <div className="site-footer__column">
              <h3>Contact</h3>

              <address className="site-footer__contact">
                <a href="mailto:info@bethelinnovations.com">
                  <span className="site-footer__contact-icon">
                    <FaEnvelope aria-hidden="true" />
                  </span>

                  <span>
                    <small>Email</small>
                    info@bethelinnovations.com
                  </span>
                </a>

                <a href="tel:+17034550650">
                  <span className="site-footer__contact-icon">
                    <FaPhoneAlt aria-hidden="true" />
                  </span>

                  <span>
                    <small>Phone</small>
                    (703) 455-0650
                  </span>
                </a>

                <div className="site-footer__contact-item">
                  <span className="site-footer__contact-icon">
                    <FaMapMarkerAlt aria-hidden="true" />
                  </span>

                  <span>
                    <small>Serving</small>
                    Government and enterprise clients
                  </span>
                </div>
              </address>
            </div>
          </div>

          {/* BOTTOM BAR */}
          <div className="site-footer__bottom">
            <p>
              © {year} Bethel Innovations, LLC. All rights reserved.
            </p>

            <div className="site-footer__legal">
              <Link to="/privacy-policy">Privacy Policy</Link>

              <span aria-hidden="true">•</span>

              <Link to="/accessibility">Accessibility</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;